/**
 * Isolierter Konvergenz-Rechner: Soll-Unternehmerstundenlohn ↔ Ist (Zirkelbezug).
 * Keine Store-Writes — reine Mathematik.
 */

export type UnternehmerlohnConvergenceParams = {
  isSolo: boolean;
  echterBedarfGesamt: number;
  chefMonatsstunden: number;
  /** Sollumsatz ohne prod./unprod. Unternehmerlohn (fix) */
  sollumsatzOhneUnternehmerlohn: number;
  produktiveLohnkostenMitarbeiterOhneAzubis: number;
  /** Optionaler abweichender Mitarbeiter-Anteil im Lohnfaktor-Nenner (z. B. IT_BZ Netto-Basis statt Costo Aziendale) */
  lohnfaktorNennerMitarbeiterOhneAzubis?: number;
  kalkulierterUnternehmerUmsatzBrutto: number;
  mwstSatz: number;
  sicherheitsfaktorPercent: number;
  urlaubsausgleichPercent: number;
  /** Startnäherung (meist aktueller Ist-Stundenlohn) */
  initialGuess?: number;
};

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Ein Iterationsschritt: aus Kandidaten-Soll den resultierenden Ist-Stundenlohn ableiten
 * (gleiche Kette wie calculations.ts → realisierterStundenlohnChef).
 */
export function evaluateIstStundenlohnFromSoll(
  sollStundenlohn: number,
  p: Omit<UnternehmerlohnConvergenceParams, 'isSolo' | 'initialGuess'>
): number {
  const hours = p.chefMonatsstunden;
  if (!(hours > 0)) return 0;

  const prodUL = hours * sollStundenlohn;
  const unprodUL = Math.max(0, p.echterBedarfGesamt - prodUL);
  const sollumsatz = p.sollumsatzOhneUnternehmerlohn + prodUL + unprodUL;

  const lohnfortzahlung =
    p.produktiveLohnkostenMitarbeiterOhneAzubis * ((p.urlaubsausgleichPercent || 0) / 100);
  const nennerMA =
    p.lohnfaktorNennerMitarbeiterOhneAzubis !== undefined
      ? p.lohnfaktorNennerMitarbeiterOhneAzubis
      : p.produktiveLohnkostenMitarbeiterOhneAzubis;
  const produktiveKostenTabelle = nennerMA + prodUL;
  if (!(produktiveKostenTabelle > 0)) return 0;

  const kalkSollumsatzInklFortzahlung = sollumsatz + lohnfortzahlung;
  const lohnfaktorNetto = kalkSollumsatzInklFortzahlung / produktiveKostenTabelle;
  const lohnfaktorBrutto = lohnfaktorNetto * (1 + (p.mwstSatz || 0) / 100);
  const lohnfaktorFortzahlung = lohnfaktorBrutto * (1 + (p.sicherheitsfaktorPercent || 0) / 100);

  if (!(lohnfaktorFortzahlung > 0)) return 0;
  return (p.kalkulierterUnternehmerUmsatzBrutto / lohnfaktorFortzahlung) / hours;
}

/**
 * Zielwertsuche: Soll ≈ Ist (|diff| < 0.005), max. 15 Schritte.
 * Solo: echterBedarfGesamt / chefMonatsstunden (unprod = 0 bei diesem Soll).
 */
export function calculateConvergedUnternehmerStundenlohn(
  params: UnternehmerlohnConvergenceParams
): number {
  const hours = Number(params.chefMonatsstunden) || 0;
  const bedarf = Number(params.echterBedarfGesamt) || 0;

  if (!(hours > 0)) return 0;

  if (params.isSolo) {
    return round2(bedarf / hours);
  }

  let soll =
    Number(params.initialGuess) > 0
      ? Number(params.initialGuess)
      : bedarf / hours;

  const base = {
    echterBedarfGesamt: bedarf,
    chefMonatsstunden: hours,
    sollumsatzOhneUnternehmerlohn: Number(params.sollumsatzOhneUnternehmerlohn) || 0,
    produktiveLohnkostenMitarbeiterOhneAzubis:
      Number(params.produktiveLohnkostenMitarbeiterOhneAzubis) || 0,
    lohnfaktorNennerMitarbeiterOhneAzubis:
      params.lohnfaktorNennerMitarbeiterOhneAzubis !== undefined
        ? Number(params.lohnfaktorNennerMitarbeiterOhneAzubis) || 0
        : undefined,
    kalkulierterUnternehmerUmsatzBrutto:
      Number(params.kalkulierterUnternehmerUmsatzBrutto) || 0,
    mwstSatz: Number(params.mwstSatz) || 0,
    sicherheitsfaktorPercent: Number(params.sicherheitsfaktorPercent) || 0,
    urlaubsausgleichPercent: Number(params.urlaubsausgleichPercent) || 0,
  };

  for (let i = 0; i < 15; i += 1) {
    const ist = evaluateIstStundenlohnFromSoll(soll, base);
    if (!Number.isFinite(ist) || ist <= 0) break;
    if (Math.abs(soll - ist) < 0.005) {
      return round2(ist);
    }
    soll = ist;
  }

  return round2(soll);
}
